import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { getModalPortalTarget } from '@onvice/portal-ui';

type Props = {
  text: string;
  size?: number;
  className?: string;
};

// Wiederverwendbarer Info-Tooltip mit zwei Eigenschaften, die ein reiner
// CSS-Hover-Tooltip nicht leisten kann:
//   1. Rendert via Portal in document.body — wird damit nicht von
//      overflow-hidden-Parents (Cards) abgeschnitten.
//   2. Clampt seine x-Position an die Viewport-Raender — laeuft auf Mobile
//      nicht ueber den linken/rechten Rand hinaus.
//
// Hintergrund Joerg 2026-05-26 (Kiara + Alex): vorheriger CSS-only-Tooltip in
// UebersichtFuehrung wurde sowohl in kleinen GF-Karten am Card-Rand
// abgeschnitten als auch auf Mobile links abgeschnitten. Beide Symptome
// haben dieselbe Wurzel — Tooltip lebt als Sub-Element des Triggers im
// gleichen Stacking- und Overflow-Kontext.
export function InfoTooltip({ text, size = 12, className }: Props) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number; arrowLeft: number } | null>(null);
  // Trigger ist ein <span>, weil InfoTooltip oft innerhalb von <Link>/<a>
  // gerendert wird — ein <button> waere dort ungueltiges HTML
  // (interactive-in-interactive). Per role + tabIndex + Keyboard-Handler
  // bleibt der Trigger trotzdem fokussierbar und a11y-konform.
  const triggerRef = useRef<HTMLSpanElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const tooltipId = useId();

  // Panel-Breite und Viewport-Padding als feste Werte; identisch zur alten
  // Implementation (w-64 = 256px), aber wir clampen zusaetzlich an den
  // Viewport. Bei sehr kleinen Bildschirmen (< 256+padding) schrumpft das
  // Panel via max-width auf die Viewport-Breite minus padding.
  const PANEL_WIDTH = 256;
  const VIEWPORT_PADDING = 8;

  function computePosition() {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const triggerCenterX = rect.left + rect.width / 2;
    let panelLeft = triggerCenterX - PANEL_WIDTH / 2;
    const maxLeft = viewportWidth - PANEL_WIDTH - VIEWPORT_PADDING;
    const minLeft = VIEWPORT_PADDING;
    if (panelLeft > maxLeft) panelLeft = maxLeft;
    if (panelLeft < minLeft) panelLeft = minLeft;
    // Wenn Viewport zu schmal fuer Panel-Breite: einfach links anlehnen,
    // max-width auf dem Panel kuemmert sich um den Ueberlauf.
    if (viewportWidth < PANEL_WIDTH + 2 * VIEWPORT_PADDING) {
      panelLeft = VIEWPORT_PADDING;
    }
    setPos({
      left: panelLeft,
      top: rect.bottom + 4,
      arrowLeft: Math.max(8, Math.min(PANEL_WIDTH - 16, triggerCenterX - panelLeft)),
    });
  }

  useEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    computePosition();
    function onScroll() {
      computePosition();
    }
    function onResize() {
      computePosition();
    }
    function onClickOutside(e: MouseEvent) {
      const target = e.target as Node;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    // ClickOutside per Microtask-Delay: sonst feuert das Mouseup des
    // oeffnenden Klicks noch im selben Tick und das Panel klappt sofort wieder zu.
    const t = setTimeout(() => document.addEventListener('mousedown', onClickOutside), 0);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    document.addEventListener('keydown', onEsc);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onClickOutside);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <>
      <span
        ref={triggerRef}
        role="button"
        tabIndex={0}
        aria-label="Info anzeigen"
        aria-describedby={open ? tooltipId : undefined}
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          // Auf Desktop-Hover-Geraeten ist der Click ein No-Op: Hover regelt
          // Open/Close. Sonst (Touch) togglet der Click. Vor dem Fix kollidierte
          // Hover + Click — Click direkt nach Hover hat zurueck auf false getoggelt.
          if (!window.matchMedia('(pointer: fine)').matches) {
            setOpen((v) => !v);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
          }
        }}
        onMouseEnter={() => {
          if (window.matchMedia('(pointer: fine)').matches) setOpen(true);
        }}
        onMouseLeave={() => {
          if (window.matchMedia('(pointer: fine)').matches) setOpen(false);
        }}
        // Padding p-2 vergroessert den Touch-Target auf ~28-32 px,
        // ohne dass das Info-Icon visuell waechst. Apple HIG empfiehlt 44 px,
        // im Card-Header ist 28 px ein guter Kompromiss zwischen Touch-bar +
        // nicht-stoerend. -m-2 holt die externe Layout-Box wieder zurueck,
        // damit das Icon im Header-Flow nicht 16 px zusaetzlich nimmt.
        className={`inline-flex items-center justify-center cursor-help text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded p-2 -m-2 ${
          className ?? ''
        }`}
      >
        <Info size={size} />
      </span>
      {open && pos
        ? createPortal(
            <div
              ref={panelRef}
              id={tooltipId}
              role="tooltip"
              style={{
                position: 'fixed',
                left: pos.left,
                top: pos.top,
                width: PANEL_WIDTH,
                maxWidth: `calc(100vw - ${2 * VIEWPORT_PADDING}px)`,
                zIndex: 9999,
              }}
              className="bg-card border border-default rounded-lg shadow-xl p-3 text-xs text-foreground normal-case whitespace-normal font-normal"
            >
              {text}
            </div>,
            getModalPortalTarget(),
          )
        : null}
    </>
  );
}

export default InfoTooltip;
